import { endOfMonth, startOfMonth } from 'date-fns';
import { matchPath } from 'react-router';

import {
  ADVISOR_USER_GROUPS,
  ADVISOR_USERS,
  ASSIGNEE_USER_GROUPS,
  ASSIGNEE_USERS,
  AUTHORS,
  BRANDS,
  ANY_SENDERS,
  CHECK_IN_SENDERS,
  CHECK_OUT_SENDERS,
  CHECKLISTS,
  INTERVENTION_PAYMENT_STATUS,
  MODULES,
  NOT_EXIST,
  RATINGS,
  SEARCH,
  SENDER_FILTER_MODE,
  SENDERS,
  STATUS,
  STEPS,
  TAGS, SCHEDULABLE_TYPE, SORT_DIRECTION, START_DATE_FROM, START_DATE_TO
} from 'constants/FilterConstants';
import { DATE_TYPE } from 'constants/InterventionConstants';
import { PARTS, SALES, TECHNICIAN } from 'constants/ModeConstants';
import { PARTS_REMINDER, REMINDER } from 'constants/ModuleConstants';
import { PATH } from 'constants/RoutesConstants';
import { CHECK_IN_STATUS, CHECK_OUT_STATUS, INCLUDE_MANUAL_CHECK_OUT } from 'constants/SelfServiceConstants';
import * as authTypes from 'modules/auth/actionTypes';
import { filterSliceAdapter } from 'modules/global/reducers/filterSliceAdapter';
import * as actionTypes from 'modules/interventions/actionTypes';
import { getPersistedFilters } from 'services/StateLoader';
import { createReducer } from 'utils/ReduxUtils';

/** @typedef {{ id: string, displayName?: string, name? :string }} EntityFilter */

/**
 * @typedef {{
 *  displayClosed: boolean,
 *  autoRefreshResults: boolean,
 *  modules: Array<import("modules/global/typing/enums/module.enum").ModuleEnum>,
 *  text: string,
 *  authors: Array<EntityFilter>,
 *  senders: Array<EntityFilter>,
 *  assignedUserIds: Array<EntityFilter>,
 *  assignedUserGroupIds: Array<string>,
 *  serviceAdvisorUserIds: Array<EntityFilter>,
 *  serviceAdvisorUserGroupIds: Array<string>,
 *  brands: Array<EntityFilter>,
 *  ratings: Array<string>,
 *  stepIds: Array<string>,
 *  notAssigned: Array<string>,
 *  status: Array<import("@arhs-fleetback/types/entities/interventions").InterventionStatus>,
 *  paymentStatus: Array<import("constants/PaymentConstants").PaymentStatus>,
 *  startDateFrom: string,
 *  startDateTo: string,
 *  period?: 'ALL' | 'TODAY' | 'WEEK' | 'CUSTOM',
 * }} InterventionFilterReducer
 */

const persistedFilters = getPersistedFilters()?.filters?.intervention ?? {};

const emptyDateFields = {
  endDateTo: null,
  endDateFrom: null,
  [START_DATE_TO]: null,
  [START_DATE_FROM]: null,
};

const emptyDateParams = {
  [SORT_DIRECTION]: 'desc',
  sortType: 'startDate',
  [SCHEDULABLE_TYPE]: DATE_TYPE.CURRENT,
};

/**
 * @returns {InterventionFilterReducer}
 */
export const getEmptyFilters = (mode, filters = persistedFilters) => {
  /** TODO: TO REVERT
   * Temporary fix due to the fact that manual checkout filter has been released earlier with a wrong default value ([]).
   * Thus this default value has been stored in the user's localStorage.
   */
  const manualCheckoutFilter = typeof filters[mode]?.[INCLUDE_MANUAL_CHECK_OUT] === 'boolean'
    ? filters[mode][INCLUDE_MANUAL_CHECK_OUT] ?? false
    : false;

  return {
    displayClosed: false,
    pendingAdvices: false,
    autoRefreshResults: true,
    [SEARCH]: null,
    [STEPS]: [],
    [BRANDS]: [],
    [TAGS]: [],
    [STATUS]: [],
    [AUTHORS]: [],
    [MODULES]: [],
    [RATINGS]: [],
    [SENDERS]: [],
    [CHECK_IN_SENDERS]: [],
    [CHECK_OUT_SENDERS]: [],
    [ANY_SENDERS]: [],
    senderFilterMode: SENDER_FILTER_MODE.EXACT,
    [NOT_EXIST]: [],
    [CHECKLISTS]: [],
    [ADVISOR_USERS]: [],
    [ASSIGNEE_USERS]: [],
    [ADVISOR_USER_GROUPS]: [],
    [INTERVENTION_PAYMENT_STATUS]: [],
    [ASSIGNEE_USER_GROUPS]: [],
    [CHECK_IN_STATUS]: [],
    [CHECK_OUT_STATUS]: [],
    ...emptyDateFields,
    ...emptyDateParams,
    ...(filters[mode] ?? {}),
    [INCLUDE_MANUAL_CHECK_OUT]: manualCheckoutFilter
  };
};

const resetFilters = () => ({
  pendingAdvices: false,
  [STEPS]: [],
  [BRANDS]: [],
  [TAGS]: [],
  [STATUS]: [],
  [AUTHORS]: [],
  [SENDERS]: [],
  [CHECK_IN_SENDERS]: [],
  [CHECK_OUT_SENDERS]: [],
  [ANY_SENDERS]: [],
  senderFilterMode: SENDER_FILTER_MODE.EXACT,
  [RATINGS]: [],
  [MODULES]: [],
  [NOT_EXIST]: [],
  [CHECKLISTS]: [],
  [INTERVENTION_PAYMENT_STATUS]: [],
  [ASSIGNEE_USERS]: [],
  [ASSIGNEE_USER_GROUPS]: [],
  [ADVISOR_USERS]: [],
  [ADVISOR_USER_GROUPS]: [],
  [CHECK_IN_STATUS]: [],
  [CHECK_OUT_STATUS]: [],
  [INCLUDE_MANUAL_CHECK_OUT]: false,
});

const emptyState = {
  [PARTS]: getEmptyFilters(PARTS),
  [SALES]: getEmptyFilters(SALES),
  [REMINDER]: getEmptyFilters(REMINDER),
  [TECHNICIAN]: getEmptyFilters(TECHNICIAN),
  [PARTS_REMINDER]: getEmptyFilters(PARTS_REMINDER),
};

const DASHBOARD_PATHS = [PATH.SALES, PATH.REMINDERS, PATH.AFTERSALES, PATH.BODYWORKS, PATH.PARTS, PATH.PARTS_REMINDER];

const isDashboardLocation = () => DASHBOARD_PATHS.some(({ path }) => matchPath(path, document.location.pathname));

/**
 * @param {string} pathname
 * @returns {import("modules/global/typing/enums/modes.enum").ModeEnum}
 */
const getModeFromPathname = pathname => {
  const CONFIG = {
    [SALES]: [PATH.SALES],
    [PARTS]: [PATH.PARTS],
    [REMINDER]: [PATH.REMINDERS],
    [PARTS_REMINDER]: [PATH.PARTS_REMINDER],
    [TECHNICIAN]: [PATH.AFTERSALES, PATH.BODYWORKS, PATH.SELF_SERVICE_KANBAN],
  };

  const [mode] = Object.entries(CONFIG).find(([, paths]) => paths.some(currentPath => matchPath(currentPath.path, pathname))) ?? [];
  return /** @type {import("modules/global/typing/enums/modes.enum").ModeEnum} */ (mode);
};

const getCurrentMode = () => getModeFromPathname(document.location.pathname);

const filterAdapter = filterSliceAdapter(emptyState, persistedFilters);

const loadSession = (state) => {
  const filters = getPersistedFilters()?.filters?.intervention ?? {};
  let newState = Object.keys(state).reduce((acc, mode) => ({
    ...acc,
    [mode]: {
      ...state[mode],
      ...getEmptyFilters(mode, filters),
      displayClosed: filters?.[mode]?.displayClosed ?? false,
      autoRefreshResults: filters?.[mode]?.autoRefreshResults ?? true,
    },
  }), {});

  if (isDashboardLocation()) {
    const mode = getCurrentMode();
    if (newState[mode]) {
      newState = {
        ...newState,
        [mode]: {
          ...newState[mode],
          [SEARCH]: new URLSearchParams(document.location.search).get(SEARCH) ?? newState[mode][SEARCH],
        },
      };
    }
  }
  return newState;
};

const toggleQuickFilter = (state, action) => {
  const mode = getCurrentMode();
  if (state[mode]) {
    return {
      ...state,
      [mode]: {
        ...state[mode],
        [action.payload]: !state[mode][action.payload],
      },
    };
  }
  return state;
};

const setInterventionsFilters = (state, action) => {
  const mode = getCurrentMode();
  if (state[mode]) {
    return {
      ...state,
      [mode]: {
        ...state[mode],
        [action.payload.field]: action.payload.value,
      },
    };
  }
  return state;
};

const setSenderFilterMode = (state, action) => {
  const mode = getCurrentMode();
  if (state[mode]) {
    return {
      ...state,
      [mode]: {
        ...state[mode],
        senderFilterMode: action.payload,
      },
    };
  }
  return state;
};

const removeInterventionFilter = (state, action) => {
  const mode = getCurrentMode();
  const baseFilters = resetFilters();
  const { field, value } = action.payload;
  const isArrayFilters = Array.isArray(baseFilters[field]);

  if (state[mode]) {
    let newFieldValue;
    if (isArrayFilters) {
      newFieldValue = state[mode][field]?.filter(filter => {
        const type = typeof filter;
        if (type === 'string' && Array.isArray(value)) {
          return !value.includes(filter);
        }
        return type === 'object' ? filter.id !== value : filter !== value;
      }) ?? [];
    } else {
      newFieldValue = baseFilters[field];
    }

    return {
      ...state,
      [mode]: {
        ...state[mode],
        [field]: newFieldValue,
      },
    };
  }
  return state;
};

const clearInterventionsFilters = (state) => {
  const mode = getCurrentMode();
  if (state[mode]) {
    return {
      ...state,
      [mode]: {
        ...state[mode],
        ...resetFilters(),
        displayClosed: false,
      },
    };
  }
  return state;
};

const clearInterventionsTemporaryFilters = (state) => Object.keys(state).reduce((acc, mode) => ({
  ...acc,
  [mode]: {
    ...state[mode],
    ...resetFilters(),
  },
}), {});

const setSchedulableType = (state, action) => {
  const mode = getCurrentMode();
  const existingFilter = state[mode];

  if (existingFilter) {
    const intermediateFilter = { ...existingFilter, ...emptyDateFields };

    let specificDateUpdates = {};
    if (action.payload === DATE_TYPE.SPECIFIC) {
      const isStartDate = intermediateFilter.sortType === 'startDate';
      specificDateUpdates = {
        [isStartDate ? 'startDateFrom' : 'endDateFrom']: startOfMonth(new Date()).toISOString(),
        [isStartDate ? 'startDateTo' : 'endDateTo']: endOfMonth(new Date()).toISOString(),
      };
    }

    return {
      ...state,
      [mode]: {
        ...existingFilter,
        ...({
          ...emptyDateFields,
          ...specificDateUpdates,
          schedulableType: action.payload,
          sortDirection: action.payload === DATE_TYPE.PLANNED ? 'asc' : 'desc',
        }),
      },
    };
  }
  return state;
};

const setSort = (state, action) => {
  const { withEmptyDate = true } = action.payload;
  const mode = getCurrentMode();

  if (state[mode]) {
    return {
      ...state,
      [mode]: {
        ...state[mode],
        ...({
          ...(withEmptyDate ? { ...emptyDateFields, ...emptyDateParams } : {}),
          sortType: action.payload.type,
          sortDirection: action.payload.direction,
        }),
      },
    };
  }
  return state;
};

export default createReducer(filterAdapter.getInitialState(), {
  [authTypes.LOAD_SESSION_SUCCESS]: loadSession,
  [authTypes.SIGN_IN_SUCCESS]: loadSession,
  [actionTypes.TOGGLE_QUICK_FILTER]: toggleQuickFilter,
  [actionTypes.SET_INTERVENTIONS_FILTERS]: setInterventionsFilters,
  [actionTypes.SET_INTERVENTIONS_SENDER_FILTER_MODE]: setSenderFilterMode,
  [actionTypes.REMOVE_INTERVENTION_FILTER]: removeInterventionFilter,
  [actionTypes.CLEAR_INTERVENTIONS_FILTERS]: clearInterventionsFilters,
  [actionTypes.CLEAR_INTERVENTIONS_TEMPORARY_FILTERS]: clearInterventionsTemporaryFilters,
  [actionTypes.SET_SCHEDULABLE_TYPE]: setSchedulableType,
  [actionTypes.SET_SORT]: setSort,
});
